import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Search, Trash2 } from 'lucide-react'
import { apiMethods } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import Input from '../components/Input'
import Button from '../components/Button'

const STAGE_OPTIONS = [
  { value: '', label: 'All stages' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'disqualified', label: 'Disqualified' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
]

const GOOGLE_IMPACT_STAGES = new Set(['qualified', 'won'])

function formatDateTime(value) {
  if (!value) return '-'

  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

function stageBadgeClass(stage, impactsGoogle) {
  if (stage === 'qualified') {
    return impactsGoogle
      ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-300'
      : 'bg-emerald-100 text-emerald-700'
  }

  if (stage === 'won') {
    return impactsGoogle
      ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-300'
      : 'bg-green-100 text-green-700'
  }

  const classes = {
    new: 'bg-gray-100 text-gray-700',
    contacted: 'bg-blue-100 text-blue-700',
    disqualified: 'bg-red-100 text-red-700',
    proposal_sent: 'bg-indigo-100 text-indigo-700',
    lost: 'bg-slate-200 text-slate-700',
  }

  return classes[stage] || 'bg-gray-100 text-gray-700'
}

function stageLabel(stage, impactsGoogle) {
  return impactsGoogle && GOOGLE_IMPACT_STAGES.has(stage) ? `${stage} !` : stage
}

function hasGoogleAdsConnection(lead) {
  if (typeof lead?.has_google_ads_connection === 'boolean') {
    return lead.has_google_ads_connection
  }

  return [lead?.gclid, lead?.gbraid, lead?.wbraid].some(
    (value) => typeof value === 'string' && value.trim() !== '',
  )
}

export default function Leads() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isSuperAdmin } = useAuthStore()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [stage, setStage] = useState('')
  const [syncMessage, setSyncMessage] = useState('')

  const queryParams = useMemo(
    () => ({
      page,
      per_page: 20,
      search: search || undefined,
      stage: stage || undefined,
    }),
    [page, search, stage],
  )

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['leads', queryParams],
    queryFn: () => apiMethods.getLeads(queryParams),
  })

  const wpSyncMutation = useMutation({
    mutationFn: () => apiMethods.dispatchWpLeadSync({ force: true, limit: 25 }),
    onSuccess: (response) => {
      const syncData = response?.data?.body?.data
      const processed = syncData?.processed ?? 0
      const sent = syncData?.sent ?? 0
      const failed = syncData?.failed ?? 0

      setSyncMessage(`WP sync finished: processed ${processed}, sent ${sent}, failed ${failed}.`)
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Manual WordPress sync failed.'

      setSyncMessage(message)
    },
  })

  const deleteLeadMutation = useMutation({
    mutationFn: (leadId) => apiMethods.deleteLead(leadId),
    onSuccess: () => {
      setSyncMessage('Lead deleted successfully.')
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['lead-events'] })
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete lead.'

      setSyncMessage(message)
    },
  })

  const handleDeleteLead = (event, lead) => {
    event.stopPropagation()

    const identifier = lead.full_name || lead.email || lead.lead_uuid || `#${lead.id}`
    const confirmed = window.confirm(
      `Delete lead "${identifier}" and all stage history permanently? This cannot be undone.`,
    )

    if (!confirmed) {
      return
    }

    deleteLeadMutation.mutate(lead.id)
  }

  if (!isSuperAdmin()) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        You do not have permission to access Leads.
      </div>
    )
  }

  const leads = data?.data || []
  const meta = data?.meta
  const hasAnyGoogleConnectedLeads = leads.some((lead) => hasGoogleAdsConnection(lead))

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
          <p className="mt-1 text-sm text-gray-500">
            Review captured leads and manage CRM stage progression.
          </p>
        </div>

        <Button
          type="button"
          onClick={() => wpSyncMutation.mutate()}
          disabled={wpSyncMutation.isPending}
        >
          {wpSyncMutation.isPending ? 'Syncing...' : 'Sync Pending WP Leads'}
        </Button>
      </div>

      {syncMessage ? (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
          {syncMessage}
        </div>
      ) : null}

      <div className="rounded-lg bg-white p-4 shadow">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="relative md:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              className="pl-10"
              value={search}
              onChange={(event) => {
                setPage(1)
                setSearch(event.target.value)
              }}
              placeholder="Search by name, email, phone, company"
            />
          </div>

          <select
            className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            value={stage}
            onChange={(event) => {
              setPage(1)
              setStage(event.target.value)
            }}
          >
            {STAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {hasAnyGoogleConnectedLeads ? (
          <p className="mt-3 text-xs font-medium text-amber-700">
            ! = may create Google upload event; not proof of processing or attribution
          </p>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Lead
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Submitted
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Stage
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Value
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Attribution
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {isLoading ? (
                <tr>
                  <td className="px-4 py-10 text-center text-sm text-gray-500" colSpan={6}>
                    Loading leads...
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td className="px-4 py-10 text-center text-sm text-gray-500" colSpan={6}>
                    No leads found.
                  </td>
                </tr>
              ) : (
                leads.map((lead) => {
                  const leadHasGoogleAdsConnection = hasGoogleAdsConnection(lead)

                  return (
                    <tr
                      key={lead.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/leads/${lead.id}`)}
                    >
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{lead.full_name || '-'}</div>
                        <div className="text-gray-500">{lead.email || lead.phone || '-'}</div>
                        <div className="text-xs text-gray-400">{lead.company_name || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDateTime(lead.submitted_at_utc)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${stageBadgeClass(
                              lead.current_stage,
                              leadHasGoogleAdsConnection,
                            )}`}
                          >
                            {stageLabel(lead.current_stage, leadHasGoogleAdsConnection)}
                          </span>
                          {lead.has_retracted_event ? (
                            <span className="inline-flex rounded-full bg-rose-100 px-2 py-1 text-xs font-medium text-rose-800 ring-1 ring-rose-300">
                              {leadHasGoogleAdsConnection ? 'Retracted !' : 'Retracted'}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div>Qualified: {lead.qualified_value ?? '-'}</div>
                        <div>Won: {lead.won_value ?? '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="text-xs">
                          Google Ads usable match data: {leadHasGoogleAdsConnection ? 'yes' : 'no'}
                        </div>
                        <div className="text-xs">consent: {lead.ad_user_data_consent ? 'yes' : 'no'}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-sm">
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                          title="Delete lead"
                          onClick={(event) => handleDeleteLead(event, lead)}
                          disabled={deleteLeadMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
          <div className="text-sm text-gray-500">
            {meta
              ? `Page ${meta.current_page} of ${meta.last_page} (${meta.total} leads)`
              : 'No pagination data'}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={!meta || page <= 1 || isFetching}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
              onClick={() => setPage((current) => current + 1)}
              disabled={!meta || page >= meta.last_page || isFetching}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
